import { useState } from 'react'
import { AlertTriangle, XCircle } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import { toast } from 'sonner'

export function LossConfirmationModal({
  open,
  onClose,
  dealId: _dealId,
  onLost,
  saving = false,
}: {
  open: boolean
  onClose: () => void
  dealId: string
  onLost: (updates: {
    motivoPerda: string
    submotivoPerda?: string
    comentarioPerda?: string
  }) => void | Promise<void>
  saving?: boolean
}) {
  const [motivoPerda, setMotivoPerda] = useState('')
  const [submotivoPerda, setSubmotivoPerda] = useState('')
  const [comentarioPerda, setComentarioPerda] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const lossReasons = [
    'Sem orçamento',
    'Escolheu concorrente',
    'Sem resposta',
    'Não qualificado',
    'Timing inadequado',
    'Outro',
  ]

  const handleConfirm = async () => {
    if (!motivoPerda) {
      toast.error('Selecione o motivo da perda')
      return
    }
    setSubmitting(true)
    try {
      await onLost({
        motivoPerda,
        submotivoPerda: submotivoPerda || undefined,
        comentarioPerda: comentarioPerda || undefined,
      })
      onClose()
    } catch {
      toast.error('Erro ao marcar como perdido')
    } finally {
      setSubmitting(false)
    }
  }

  const isSaving = saving || submitting

  return (
    <Modal open={open} onClose={onClose} title="Marcar como Perdido" size="sm">
      <div className="space-y-4">
        <div className="flex items-center gap-3 p-3 rounded-md bg-amber-400/5 border border-amber-400/10">
          <AlertTriangle size={20} className="text-amber-400 shrink-0" />
          <p className="text-xs text-text-label">
            Ao marcar como perdido, o deal sairá do pipeline ativo. Esta ação pode ser revertida manualmente.
          </p>
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-[10px] text-text-label uppercase tracking-wider font-medium">
            Motivo da Perda <span className="text-red-400">*</span>
          </label>
          <select
            value={motivoPerda}
            onChange={(e) => setMotivoPerda(e.target.value)}
            disabled={isSaving}
            className="w-full bg-white/[0.04] border border-white/[0.06] text-text-primary text-sm rounded-md px-3 py-2 outline-none focus:border-accent cursor-pointer"
          >
            <option value="">Selecionar motivo...</option>
            {lossReasons.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-[10px] text-text-label uppercase tracking-wider font-medium">Submotivo</label>
          <input
            value={submotivoPerda}
            onChange={(e) => setSubmotivoPerda(e.target.value)}
            disabled={isSaving}
            placeholder="Ex: Concorrente X, preço 30% menor..."
            className="w-full bg-white/[0.04] border border-white/[0.06] text-text-primary text-sm rounded-md px-3 py-2 outline-none focus:border-accent"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-[10px] text-text-label uppercase tracking-wider font-medium">Comentário</label>
          <textarea
            value={comentarioPerda}
            onChange={(e) => setComentarioPerda(e.target.value)}
            disabled={isSaving}
            rows={3}
            placeholder="Detalhes sobre a perda..."
            className="w-full bg-white/[0.04] border border-white/[0.06] text-text-primary text-sm rounded-md px-3 py-2 outline-none focus:border-accent resize-none"
          />
        </div>

        <div className="flex gap-2 pt-2">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={isSaving} className="flex-1">
            Cancelar
          </Button>
          <Button
            variant="destructive"
            size="sm"
            icon={<XCircle size={13} />}
            loading={isSaving}
            onClick={handleConfirm}
            className="flex-1"
          >
            Confirmar Perda
          </Button>
        </div>
      </div>
    </Modal>
  )
}
